import React from 'react';
import { Link } from '@inertiajs/react';
import MainLayout from '../../layouts/MainLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { FiHome, FiEdit2, FiArrowLeft, FiMapPin, FiDollarSign, FiUsers, FiCalendar, FiPlus, FiTrash2, FiUser } from 'react-icons/fi';

export default function FacilityShow({ facility, user }) {
  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount / 100);
  };

  return (
    <MainLayout user={user}>
      <div className="py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-6">
            <Button variant="outline" className="mb-4" asChild>
              <Link href="/facility_owner/facilities">
                <FiArrowLeft className="mr-2 h-4 w-4" />
                Back to Facilities
              </Link>
            </Button>

            <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-gray-900">{facility.name}</h1>
                <p className="text-gray-500">
                  {facility.organization?.name || 'No organization'} ·
                  <Badge variant={facility.published ? "success" : "secondary"} className="ml-2">
                    {facility.published ? "Published" : "Draft"}
                  </Badge>
                </p>
              </div>
              <div className="mt-4 md:mt-0 flex space-x-3">
                <Button variant="outline" asChild>
                  <Link href={`/facilities/${facility.slug}`} target="_blank">
                    View Public Page
                  </Link>
                </Button>
                <Button variant="outline" className="bg-amber-50 text-amber-600 border-amber-200 hover:bg-amber-100 hover:text-amber-700" asChild>
                  <Link href={`/facility_owner/analytics/${facility.id}`}>
                    <FiUsers className="mr-2 h-4 w-4" />
                    View Analytics
                  </Link>
                </Button>
                <Button asChild>
                  <Link href={`/facility_owner/facilities/${facility.id}/edit`}>
                    <FiEdit2 className="mr-2 h-4 w-4" />
                    Edit Facility
                  </Link>
                </Button>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <Tabs defaultValue="details" className="w-full">
                <TabsList className="grid w-full grid-cols-4">
                  <TabsTrigger value="details">Details</TabsTrigger>
                  <TabsTrigger value="units">Units</TabsTrigger>
                  <TabsTrigger value="leads">Leads</TabsTrigger>
                  <TabsTrigger value="tours">Tours</TabsTrigger>
                </TabsList>

                <TabsContent value="details">
                  <Card>
                    <CardHeader>
                      <CardTitle>Facility Details</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {facility.main_photo_url && (
                        <div className="mb-4">
                          <img
                            src={facility.main_photo_url}
                            alt={facility.name}
                            className="w-full h-64 object-cover rounded-md"
                          />
                        </div>
                      )}

                      <div>
                        <h3 className="text-sm font-medium text-gray-500">Description</h3>
                        <p className="mt-1">{facility.description || 'No description provided.'}</p>
                      </div>

                      <div>
                        <h3 className="text-sm font-medium text-gray-500">Address</h3>
                        <p className="mt-1 flex items-center">
                          <FiMapPin className="mr-2 h-4 w-4 text-gray-400" />
                          {facility.address || 'No address provided.'}
                        </p>
                      </div>

                      <div>
                        <h3 className="text-sm font-medium text-gray-500">Care Type</h3>
                        <p className="mt-1">{facility.care_type || 'Not specified'}</p>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>

                <TabsContent value="units">
                  <Card>
                    <CardHeader>
                      <div className="flex justify-between items-center">
                        <div>
                          <CardTitle>Units</CardTitle>
                          <CardDescription>
                            {facility.units?.length || 0} unit types available
                          </CardDescription>
                        </div>
                        <Button asChild>
                          <Link href={`/facility_owner/units/new?facility_id=${facility.id}`}>
                            <FiPlus className="mr-2 h-4 w-4" />
                            Add Unit
                          </Link>
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent>
                      {facility.units && facility.units.length > 0 ? (
                        <div className="overflow-x-auto">
                          <table className="w-full">
                            <thead>
                              <tr className="bg-gray-50 border-b border-gray-200">
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Unit Type</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Bedrooms</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Bathrooms</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Monthly Price</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Available</th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {facility.units.map((unit) => (
                                <tr key={unit.id} className="hover:bg-gray-50">
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="font-medium text-gray-900">
                                      {unit.room_type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                    </div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-gray-500">
                                    {unit.bedrooms}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-gray-500">
                                    {unit.bathrooms}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-gray-500">
                                    {formatCurrency(unit.monthly_cost_cents)}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <Badge variant={unit.available_count > 0 ? "success" : "secondary"}>
                                      {unit.available_count}
                                    </Badge>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                    <div className="flex justify-end space-x-2">
                                      <Button variant="outline" size="sm" className="flex items-center" asChild>
                                        <Link href={`/facility_owner/units/${unit.id}/edit`}>
                                          <FiEdit2 className="h-4 w-4" />
                                        </Link>
                                      </Button>

                                      <Link
                                        href={`/facility_owner/units/${unit.id}`}
                                        method="delete"
                                        as="button"
                                        className="inline-flex items-center justify-center h-8 px-2 py-1 text-sm font-medium border rounded-md border-gray-300 bg-white hover:bg-gray-50 text-gray-700"
                                      >
                                        <FiTrash2 className="h-4 w-4 text-red-500" />
                                      </Link>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div className="text-center py-6">
                          <p className="text-gray-500">No units added yet.</p>
                          <Button className="mt-4" asChild>
                            <Link href={`/facility_owner/units/new?facility_id=${facility.id}`}>
                              <FiPlus className="mr-2 h-4 w-4" />
                              Add Unit
                            </Link>
                          </Button>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>

                <TabsContent value="leads">
                  <Card>
                    <CardHeader>
                      <CardTitle>Recent Leads</CardTitle>
                    </CardHeader>
                    <CardContent>
                      {facility.leads && facility.leads.length > 0 ? (
                        <div className="space-y-4">
                          {facility.leads.slice(0, 5).map((lead) => (
                            <div key={lead.id} className="flex justify-between items-center p-3 border rounded-md">
                              <div>
                                <p className="font-medium">{lead.user?.email || 'Anonymous'}</p>
                                <p className="text-sm text-gray-500">
                                  <FiCalendar className="inline mr-1 h-3 w-3" />
                                  {new Date(lead.created_at).toLocaleDateString()}
                                </p>
                              </div>
                              <div className="flex items-center">
                                <Badge variant={
                                  lead.status === 'new' ? 'secondary' :
                                  lead.status === 'contacted' ? 'default' :
                                  lead.status === 'qualified' ? 'success' :
                                  lead.status === 'converted' ? 'success' :
                                  'destructive'
                                }>
                                  {lead.status}
                                </Badge>
                                <Button variant="ghost" size="sm" asChild className="ml-2">
                                  <Link href={`/facility_owner/leads/${lead.id}`}>
                                    View
                                  </Link>
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-6">
                          <p className="text-gray-500">No leads yet.</p>
                        </div>
                      )}
                    </CardContent>
                    {facility.leads && facility.leads.length > 0 && (
                      <CardFooter>
                        <Button variant="outline" asChild>
                          <Link href="/facility_owner/leads">
                            View All Leads
                          </Link>
                        </Button>
                      </CardFooter>
                    )}
                  </Card>
                </TabsContent>

                <TabsContent value="tours">
                  <Card>
                    <CardHeader>
                      <CardTitle>Upcoming Tours</CardTitle>
                    </CardHeader>
                    <CardContent>
                      {facility.tours && facility.tours.length > 0 ? (
                        <div className="space-y-4">
                          {facility.tours.slice(0, 5).map((tour) => (
                            <div key={tour.id} className="flex justify-between items-center p-3 border rounded-md">
                              <div>
                                <p className="font-medium">
                                  <FiCalendar className="inline mr-1 h-3 w-3" />
                                  {new Date(tour.scheduled_at).toLocaleDateString()} at {new Date(tour.scheduled_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                                </p>
                                <p className="text-sm text-gray-500">
                                  <FiUsers className="inline mr-1 h-3 w-3" />
                                  {tour.user?.email || 'Anonymous'}
                                </p>
                              </div>
                              <div className="flex items-center">
                                <Badge variant={
                                  tour.status === 'pending' ? 'secondary' :
                                  tour.status === 'confirmed' ? 'success' :
                                  tour.status === 'completed' ? 'default' :
                                  'destructive'
                                }>
                                  {tour.status}
                                </Badge>
                                <Button variant="ghost" size="sm" asChild className="ml-2">
                                  <Link href={`/facility_owner/tours/${tour.id}`}>
                                    View
                                  </Link>
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-6">
                          <p className="text-gray-500">No upcoming tours.</p>
                        </div>
                      )}
                    </CardContent>
                    {facility.tours && facility.tours.length > 0 && (
                      <CardFooter>
                        <Button variant="outline" asChild>
                          <Link href="/facility_owner/tours">
                            View All Tours
                          </Link>
                        </Button>
                      </CardFooter>
                    )}
                  </Card>
                </TabsContent>
              </Tabs>
            </div>

            <div>
              <Card>
                <CardHeader>
                  <CardTitle>Statistics</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex justify-between items-center p-3 bg-blue-50 rounded-md">
                    <div className="flex items-center">
                      <div className="bg-blue-100 p-2 rounded-full mr-3">
                        <FiUsers className="h-5 w-5 text-blue-600" />
                      </div>
                      <span>Total Leads</span>
                    </div>
                    <span className="font-bold text-lg">{facility.leads_count || 0}</span>
                  </div>

                  <div className="flex justify-between items-center p-3 bg-green-50 rounded-md">
                    <div className="flex items-center">
                      <div className="bg-green-100 p-2 rounded-full mr-3">
                        <FiCalendar className="h-5 w-5 text-green-600" />
                      </div>
                      <span>Total Tours</span>
                    </div>
                    <span className="font-bold text-lg">{facility.tours_count || 0}</span>
                  </div>

                  <div className="flex justify-between items-center p-3 bg-purple-50 rounded-md">
                    <div className="flex items-center">
                      <div className="bg-purple-100 p-2 rounded-full mr-3">
                        <FiHome className="h-5 w-5 text-purple-600" />
                      </div>
                      <span>Occupancy Rate</span>
                    </div>
                    <span className="font-bold text-lg">
                      {facility.occupancy_rate ? `${facility.occupancy_rate}%` : 'N/A'}
                    </span>
                  </div>

                  <div className="flex justify-between items-center p-3 bg-amber-50 rounded-md">
                    <div className="flex items-center">
                      <div className="bg-amber-100 p-2 rounded-full mr-3">
                        <FiDollarSign className="h-5 w-5 text-amber-600" />
                      </div>
                      <span>Avg. Monthly Cost</span>
                    </div>
                    <span className="font-bold text-lg">
                      {facility.avg_monthly_cost ? formatCurrency(facility.avg_monthly_cost) : 'N/A'}
                    </span>
                  </div>
                </CardContent>
              </Card>

              <Card className="mt-6">
                <CardHeader>
                  <CardTitle>Quick Actions</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <Button className="w-full justify-start" variant="outline" asChild>
                    <Link href={`/facility_owner/facilities/${facility.id}/edit`}>
                      <FiEdit2 className="mr-2 h-4 w-4" />
                      Edit Facility Details
                    </Link>
                  </Button>

                  <Button className="w-full justify-start" variant="outline" asChild>
                    <Link href={`/facility_owner/units/new?facility_id=${facility.id}`}>
                      <FiHome className="mr-2 h-4 w-4" />
                      Add New Unit
                    </Link>
                  </Button>

                  <Button className="w-full justify-start" variant="outline" asChild>
                    <Link href={`/facility_owner/tour_slots/new?facility_id=${facility.id}`}>
                      <FiCalendar className="mr-2 h-4 w-4" />
                      Add Tour Slot
                    </Link>
                  </Button>

                  <Button className="w-full justify-start" variant="outline" asChild>
                    <Link href={`/facility_owner/leads?facility_id=${facility.id}`}>
                      <FiUser className="mr-2 h-4 w-4" />
                      View Facility Leads
                    </Link>
                  </Button>

                  <Button className="w-full justify-start" variant="outline" asChild>
                    <Link href={`/facility_owner/tours?facility_id=${facility.id}`}>
                      <FiCalendar className="mr-2 h-4 w-4" />
                      View Facility Tours
                    </Link>
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
}
